import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  CBadge,
  CButton,
  CForm,
  CFormTextarea,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CFormSelect,
} from "@coreui/react-pro";
import toast, { Toaster } from "react-hot-toast";
import OrderSingle from "../Components/OrderSingle";
import {
  AllWordFirstChar,
  capitalizeWords,
  changeGMTFormate,
  formatTimestamp,
} from "../Components/allFunctions";
import { CircleLoader } from "react-spinners";
import Constants from "../Constants";
import { authFetch } from "../utils/authFetch";
import { COUNTRIES } from "../utils/countries";
import { isValidTrackingUrl, TRACKING_URL_ERROR } from "../utils/trackingUrl";

const ADMIN_ENDPOINT = Constants.AUTH_BASE_URL + "/api/v1/admin";

// Our name for an ISO2 code, or undefined when the code is not in the list.
const countryName = (code) =>
  COUNTRIES.find((c) => c.code === String(code || "").toUpperCase())?.name;

// The insole models support may switch a repeated pair to. `sku` is what the
// backend validates (Orders::ReorderChanges::MODELS = the Shopify variant SKUs);
// keep the two lists identical. "The Response" has always been SKU Performance.
const REORDER_MODELS = [
  { sku: "Everyday", label: "The Everyday" },
  { sku: "Performance", label: "The Response" },
  { sku: "Luxe", label: "The Luxe" },
  { sku: "Blade", label: "The Blade" },
  { sku: "Carve", label: "The Carve" },
];

const OrderDetailsPage = (props) => {
  const lang = props.Language;
  const navigate = useNavigate();

  const params = useParams();
  const [modalVisible, setModalVisible] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  // "b2c" (a consumer's own order) or "b2b" (a team order). Reorder is offered on
  // consumer orders only: a team order is repeated from the dashboard roster.
  const [orderType, setOrderType] = useState("");
  // Support's "same insoles again": repeats this order's pairs, sizes included,
  // into a Shopify checkout and hands back a durable link to send the customer.
  const [reorderModal, setReorderModal] = useState({
    visible: false,
    loading: false,
    discountCode: "",
    // One entry per pair: { pairs, finish }, editable. Size, gender, nickname and
    // the shoe are copied as made. Mirrors Orders::ReorderChanges on the backend,
    // which validates the same five models and the 1–4 range.
    changes: [],
    result: null,
  });
  const [OrderFound, setOrderFound] = useState(true);
  const [Orderjson, setOrderJson] = useState([]);
  const [OrderDBData, setOrderDBData] = useState([]);
  const [fulfilled, setFulfilled] = useState("");
  const [OrderDate, setOrderDate] = useState("");
  const [note, setNote] = useState([]);
  // Special modifications: what the TRAINER asked us to change about specific
  // pairs. Read-only in this console, always — the factory never edits them
  // (confirmed with Dan, 2026-09-08), and there is no write path for them in the
  // admin API. They live in their own columns precisely so that saving the Note
  // below, which IS the factory's own field, can never overwrite them.
  const [specialModifications, setSpecialModifications] = useState([]);
  // Shopify's own order note: typed by the customer at checkout or written by
  // an app (Rise.ai on gift-card orders). Read-only here; it is NOT the Note
  // box below, which is the factory's. Marcela, TC-3.36 (#2169).
  const [shopifyNote, setShopifyNote] = useState("");
  const [OrderMessage, setOrderMessage] = useState({
    // flagVisible: true,
    msgValue: "",
    message: [],
    msgLoading: false,
  });
  const [OrderData, setOrderData] = useState(false);
  const [tag, setTag] = useState("");
  const [canceledFiltered, setCanceledFiltered] = useState([]);
  const [playerID, setPlayerId] = useState(null);

  // Order-level production status + tracking (per-line-item editing of these was
  // deprecated and removed — the backend only exposes order-level endpoints).
  const [productionStatus, setProductionStatus] = useState("awaiting_review");
  const [orderCancelled, setOrderCancelled] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState({ number: "", url: "", company: "" });
  const [trackingModal, setTrackingModal] = useState({
    visible: false,
    editing: false,
    loading: false,
    number: "",
    url: "",
    company: "",
  });

  //handle Update Code
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  // The countries the Shopify store ships to (ISO codes), or null for "show them
  // all": not loaded yet, or Shopify couldn't be asked. Offering every country let
  // QA save addresses the store never delivers to (Monday 12734309453).
  const [shipsTo, setShipsTo] = useState(null);

  const loadShipsTo = async () => {
    if (shipsTo) return;
    try {
      const res = await authFetch(`${ADMIN_ENDPOINT}/shipping_countries`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const body = await res.json();
      if (Array.isArray(body?.country_codes) && body.country_codes.length > 0) {
        setShipsTo(body.country_codes);
      }
    } catch {
      // Keep the full list; a lookup failure must not block an address fix.
    }
  };

  const countryOptions = shipsTo
    ? COUNTRIES.filter((c) => shipsTo.includes(c.code))
    : COUNTRIES;

  const openModal = () => {
    loadShipsTo();
    // Pre-populate form data with current shipping address
    setFormData({
      first_name: Orderjson[0]?.shipping_address?.first_name || "",
      last_name: Orderjson[0]?.shipping_address?.last_name || "",
      address1: Orderjson[0]?.shipping_address?.address1 || "",
      address2: Orderjson[0]?.shipping_address?.address2 || "",
      city: Orderjson[0]?.shipping_address?.city || "",
      province: Orderjson[0]?.shipping_address?.province || "",
      province_code: Orderjson[0]?.shipping_address?.province_code || "",
      zip: Orderjson[0]?.shipping_address?.zip || "",
      // The name comes from our list for the order's CODE, so the form never holds a name
      // and a code that disagree — that disagreement is what shipped "Boston 02110 Sweden".
      country:
        countryName(Orderjson[0]?.shipping_address?.country_code) ||
        Orderjson[0]?.shipping_address?.country ||
        "",
      country_code: Orderjson[0]?.shipping_address?.country_code || "",
      phone: Orderjson[0]?.shipping_address?.phone || "",
      company: Orderjson[0]?.shipping_address?.company || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setMessage({ text: "", type: "" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Country is picked, not typed: one choice sets the name AND the code together. It used to be
  // a free-text box with no control for the code, so the old code was re-sent under whatever
  // name was typed — "Freedonia" included — and Shopify kept the real country.
  const handleCountryChange = (e) => {
    const code = e.target.value;
    setFormData((prev) => ({
      ...prev,
      country_code: code,
      country: countryName(code) || prev.country,
      // A state/province code only means something inside its own country.
      province_code: code === prev.country_code ? prev.province_code : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authFetch(
        `${ADMIN_ENDPOINT}/orders/${params?.id}/update_shipping_address`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            shipping_address: {
              first_name: formData.first_name,
              last_name: formData.last_name,
              address1: formData.address1,
              address2: formData.address2,
              phone: formData.phone || null,
              city: formData.city,
              zip: formData.zip,
              province: formData.province,
              province_code: formData.province_code,
              country: formData.country,
              country_code: formData.country_code,
              company: formData.company,
            },
          }),
        }
      );

      if (response.ok) {
        setMessage({
          text: "Shipping address updated successfully!",
          type: "success",
        });
        setTimeout(() => {
          closeModal();
          window.location.reload();
        }, 2000);
      } else {
        const errorData = await response.json();
        setMessage({
          text:
            errorData.errors?.[0]?.message ||
            "Failed to update shipping address. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      setMessage({
        text: "Error updating shipping address: " + error.message,
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const HandleMessage = () => {
    if (OrderMessage.msgValue !== "") {
      setOrderMessage({ ...OrderMessage, msgLoading: true });

      authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body: OrderMessage.msgValue }),
      })
        .then((response) => response.json())
        .then((res) => {
          if (res.id) {
            toast.success(lang === "en" ? "Message sent" : "成功");
            const msgData = {
              ID: res.id,
              UserName: res.user_name,
              UserEmail: res.user_email,
              Message: res.body,
              MessageTime: res.created_at,
              MarkAsRead: res.read ? 1 : 0,
            };
            const newMessageArray = [...OrderMessage.message, msgData];
            setOrderMessage((prevOrderMessage) => ({
              ...prevOrderMessage,
              message: newMessageArray,
              msgValue: "",
              msgLoading: false,
            }));
          } else {
            toast.error(res.error || "Failed to send message");
            setOrderMessage({ ...OrderMessage, msgLoading: false });
          }
        })
        .catch((error) => {
          setOrderMessage({ ...OrderMessage, msgLoading: false });
          toast.error(error.message);
        });
    } else {
      toast.error(lang === "en" ? "Please fill details" : "请填写详细信息");
    }
  };

  const getMessage = () => {
    const orderId = params?.id;
    authFetch(`${ADMIN_ENDPOINT}/orders/${orderId}/messages`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        const mapped = (data || []).map((m) => ({
          ID: m.id,
          UserName: m.user_name,
          UserEmail: m.user_email,
          Message: m.body,
          MessageTime: m.created_at,
          MarkAsRead: m.read ? 1 : 0,
        }));
        setOrderMessage((prsVal) => ({ ...prsVal, message: mapped }));
      })
      .catch((error) => {
        console.log(error.message);
      });
  };

  const SingleOrder = () => {
    authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}`, { credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error("Order not found");
        return response.json();
      })
      .then((data) => {
        setOrderFound(true);

        // Build Orderjson from order_json (Shopify data) or from DB data
        const shopifyData = data.order_json || {};

        // Use shipping address from dedicated DB columns
        const shippingAddr = data.shipping_address || {};
        const shippingAddress = {
          first_name: shippingAddr.name?.split(" ")[0] || "",
          last_name: shippingAddr.name?.split(" ").slice(1).join(" ") || "",
          address1: shippingAddr.address1 || "",
          address2: shippingAddr.address2 || "",
          city: shippingAddr.city || "",
          province: shippingAddr.province || "",
          province_code: shippingAddr.province_code || "",
          zip: shippingAddr.zip || "",
          country: shippingAddr.country || "",
          country_code: shippingAddr.country_code || "",
          phone: shippingAddr.phone || "",
        };

        // Build line items from database data.
        //
        // These are the API's own typed fields, passed straight through under the
        // API's own names. There used to be a `properties: [{name, value}]` array
        // here as well — the same values re-encoded into a Shopify-shaped list so
        // OrderSingle could look them up by string-matching the name. That round
        // trip is what turned a one-word typo ("Shoe Size Gender" written,
        // "ShoeSizeGender" read) into Gender rendering blank on every line, and
        // it bought nothing: none of this is Shopify data. `line_items` below is
        // always THIS array, never the Shopify payload's, so nothing downstream
        // ever saw a real Shopify property.
        //
        // `shoe_size_gender` (not `db_sex`) is deliberate: there is exactly one
        // spelling of this concept across the whole system and it is not "sex" —
        // see the ShoeSizeGender vocabulary note in web-enterprise/CLAUDE.md.
        const lineItems = (data.line_items || []).map((li) => ({
          id: li.shopify_line_item_id || li.id,
          slug: li.slug,
          name: li.nickname || li.finish,
          quantity: li.pairs || 1,
          variant_id: li.shopify_variant_id,
          insoleScans: li.insole_scans || [],
          footScans: li.foot_scans || [],
          // The old system named a scan we never imported (its player was left
          // out of the migration). Rendered instead of the generic "missing".
          footScanNotMigrated: li.foot_scan_not_migrated || null,
          shoe_specific: !!li.shoe_name,
          shoe_name: li.shoe_name,
          shoe_brand: li.shoe_brand,
          shoe_model: li.shoe_model,
          non_standard_width: li.non_standard_width,
          shoe_size_gender: li.shoe_size_gender,
          shoe_size: li.shoe_size,
          shoe_type: li.shoe_type,
          finish: li.finish,
          nickname: li.nickname || "",
          // The old app's Height / Weight / Age, carried over on migrated pairs
          // because the old console showed them to the factory; absent on orders
          // placed here, and the rows are then not rendered.
          player_height: li.player_height || "",
          player_weight: li.player_weight || "",
          player_age: li.player_age || "",
          image: li.image,
          // Pre-rendered by the server (special_modifications_text) — this is
          // the same string every other surface shows, so the floor and the
          // trainer can never be looking at two different sentences.
          special_modifications: li.special_modifications_text || "",
        }));

        const orderObj = {
          ...shopifyData,
          name: data.shopify_order_name || data.slug,
          tags: data.tag || "",
          // The customer's own "complete your scan & fit" link (consumer orders only).
          scan_fit_link: data.scan_fit_link || null,
          production_status: data.production_status,
          cancelled: !!data.cancelled_at,
          fulfillment_status: data.fulfillment_status,
          created_at: data.created_at,
          note: data.note,
          // The ACCOUNT that owns the order, which the API resolves live from
          // order.user — not the customer block Shopify froze into order_json at
          // checkout. The two differ whenever staff place an order for someone
          // under a plus-address, or when accounts are merged afterwards: #4675
          // was checked out as marc+12@groov.me and later moved, with its scan,
          // onto the customer's own account. Showing the checkout email here sent
          // the factory to the wrong inbox, and "Reorder for this customer" uses
          // the account (its email is what the new checkout prefills), so this
          // card has to show the same thing. The snapshot is only a fallback for
          // an order whose account the API could not name.
          customer: {
            first_name:
              data.customer_name?.split(" ")[0] ||
              shopifyData.customer?.first_name ||
              "",
            last_name:
              data.customer_name?.split(" ").slice(1).join(" ") ||
              shopifyData.customer?.last_name ||
              "",
            email: data.customer_email || shopifyData.customer?.email || "",
          },
          shipping_address: shippingAddress,
          refunds: shopifyData.refunds || [],
          line_items: lineItems,
          scans: shopifyData.scans || { insolescans: { Scans: [] } },
        };
        setOrderJson([orderObj]);

        // Build OrderDBData for components that need it
        const dbData = data.line_items?.map((li) => ({
          ItemID: li.shopify_line_item_id || li.id,
          EnterpriseName: data.enterprise_name || "",
          // The old console also had a TrainerName here (a trainer person per
          // order). Trainers are not imported and the API has no such field, so
          // the only thing to show beside the enterprise is the team.
          TeamName: data.team_name || "",
          PlayerID: li.player_name || "",
          PlayerName: li.player_name || "",
          Tag: data.tag || "",
          PID: li.id,
          RepeatedPlayer: data.is_repeat_customer || false,
        })) || [];
        setOrderDBData(dbData);

        // Handle refunds
        let filteredOrders = (shopifyData.refunds || []).map((refundObj) => {
          return (refundObj?.refund_line_items || []).map(
            ({ line_item_id }) => line_item_id
          );
        });
        setCanceledFiltered(filteredOrders.flat(Infinity));

        setTag(data.tag?.split(",").includes("test") ? "test" : "");
        setOrderType(data.order_type || "");

        // Ordering customer's slug, shown next to "Customer Info" for debugging.
        // B2C: the buyer; B2B: the manager who placed the order. No customer id
        // exists in this system, so the user slug is the stable identifier.
        setPlayerId(data.customer_slug || "");

        // Order-level production status + tracking
        setProductionStatus(data.production_status || "awaiting_review");
        setOrderCancelled(!!data.cancelled_at);
        setTrackingInfo({
          number: data.tracking_number || "",
          url: data.tracking_url || "",
          company: data.tracking_company || "",
        });

        setFulfilled(
          data.fulfillment_status == null
            ? "Unfulfilled"
            : capitalizeWords(data.fulfillment_status.replace(/_/g, " "))
        );
        const date = new Date(data.created_at);
        setOrderDate(date.toLocaleString());
        setNote(data.note || "");
        setSpecialModifications(data.special_modifications_summary || []);
        setShopifyNote(data.shopify_note || "");
      })
      .catch((error) => {
        setOrderFound(false);
        toast.error(error.message);
      });
  };

  const HandleUpdate = () => {
    setTagLoading(true);
    authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ note }),
    })
      .then((response) => response.json())
      .then((data) => {
        setTagLoading(false);
        if (data.message) {
          toast.success(lang === "en" ? "Updated successfully" : "更新成功");
        }
      })
      .catch((error) => {
        setTagLoading(false);
        console.log(`Error: ${error}`);
      });
  };

  const MessageCheck = (e, index, msgId) => {
    try {
      let newArr = [...OrderMessage.message];
      newArr[index]["MarkAsRead"] = e.target.checked ? 1 : 0;
      setOrderMessage((presVal) => ({ ...presVal, newArr }));

      authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}/messages/${msgId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ read: e.target.checked }),
      })
        .then((response) => response.json())
        .then((data) => {
          toast.success(data.message || "Updated");
        })
        .catch((error) => {
          toast.error(error.message);
        });
    } catch (error) {
      toast.error(error.message);
    }
  };
  const handleDeleteOrder = async (orderID) => {
    // TODO: implement delete via new backend
    toast.error("Delete not yet implemented on new backend");
    setModalVisible(false);
  };

  const handleMarkAsTest = async () => {
    try {
      const response = await authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}/mark_as_test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      toast.success(data.message || "Marked as test");
      setTag("test");
    } catch (error) {
      toast.error(error.message || "Error marking order as test");
    }
  };
  const handleRemoveAsTest = async () => {
    try {
      const response = await authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}/remove_as_test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      toast.success(data.message || "Removed from test");
      setTag("");
    } catch (error) {
      toast.error(error.message || "Error removing test tag");
    }
  };

  const openReorder = () =>
    setReorderModal({
      visible: true,
      loading: false,
      discountCode: "",
      changes: (Orderjson[0]?.line_items || []).map((li) => ({
        pairs: li.quantity || 1,
        finish: li.finish || "",
      })),
      result: null,
    });

  const setReorderChange = (idx, patch) =>
    setReorderModal((m) => ({
      ...m,
      changes: m.changes.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));

  // POST /admin/orders/:slug/reorder. The backend refuses, in plain words, a pair
  // without a size, a model the catalogue no longer carries, or a shoe that is
  // gone; those messages are shown as-is. Nothing is created on our side until
  // the customer pays.
  const submitReorder = async () => {
    setReorderModal((m) => ({ ...m, loading: true }));
    try {
      const body = {};
      if (reorderModal.discountCode.trim() !== "") {
        body.discount_code = reorderModal.discountCode.trim();
      }
      // Sent only when something differs from the previous order, so an untouched
      // dialog is exactly "the same again" and the backend logs changed=false.
      const original = Orderjson[0]?.line_items || [];
      const changed = reorderModal.changes.some(
        (c, i) => Number(c.pairs) !== Number(original[i]?.quantity || 1) || c.finish !== (original[i]?.finish || "")
      );
      if (changed) {
        body.changes = reorderModal.changes.map((c) => ({ pairs: Number(c.pairs), finish: c.finish }));
      }
      const response = await authFetch(`${ADMIN_ENDPOINT}/orders/${params?.id}/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Could not create the reorder");
      toast.success(data.message || "Checkout ready");
      setReorderModal((m) => ({ ...m, loading: false, result: data }));
    } catch (error) {
      toast.error(error.message || "Could not create the reorder");
      setReorderModal((m) => ({ ...m, loading: false }));
    }
  };

  const copyLink = async (url, what) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${what} copied`);
    } catch (e) {
      toast.error("Could not copy — select the link and copy it by hand");
    }
  };
  const changeProductionStatus = async (e) => {
    const status = e.target.value;
    if (status === "shipped") {
      // Shipping requires tracking info; collect it, then PATCH production_status.
      setTrackingModal({ visible: true, editing: false, loading: false, number: "", url: "", company: "" });
      return;
    }
    try {
      const response = await authFetch(
        `${ADMIN_ENDPOINT}/orders/${params?.id}/production_status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ production_status: status }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(lang === "en" ? "Status updated" : "成功");
        setProductionStatus(status);
      } else {
        toast.error(data.error || "Failed to update status");
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submitTracking = async () => {
    const { editing, number, url, company } = trackingModal;
    if (!number || !url || !company) {
      toast.error(lang === "en" ? "Please fill details" : "请填写详细信息");
      return;
    }
    if (!isValidTrackingUrl(url)) {
      toast.error(lang === "en" ? TRACKING_URL_ERROR.en : TRACKING_URL_ERROR.cn);
      return;
    }
    setTrackingModal((m) => ({ ...m, loading: true }));
    try {
      // editing = update tracking on an already-shipped order's fulfillment;
      // otherwise = mark the order shipped (creates the Shopify fulfillment).
      const endpoint = editing
        ? `${ADMIN_ENDPOINT}/orders/${params?.id}/tracking`
        : `${ADMIN_ENDPOINT}/orders/${params?.id}/production_status`;
      const body = editing
        ? { tracking_number: number, tracking_url: url.trim(), tracking_company: company }
        : { production_status: "shipped", tracking_number: number, tracking_url: url.trim(), tracking_company: company };

      const response = await authFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(lang === "en" ? "Success" : "成功");
        setTrackingModal({ visible: false, editing: false, loading: false, number: "", url: "", company: "" });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setTrackingModal((m) => ({ ...m, loading: false }));
        toast.error(data.error || "Failed to update");
      }
    } catch (err) {
      setTrackingModal((m) => ({ ...m, loading: false }));
      toast.error(err.message);
    }
  };

  const openEditTracking = () => {
    setTrackingModal({
      visible: true,
      editing: true,
      loading: false,
      number: trackingInfo.number,
      url: trackingInfo.url,
      company: trackingInfo.company,
    });
  };

  const handleMarkAsSeen = async () => {
    // TODO: implement mark-as-seen on new backend if needed
    toast.success("Marked as seen");
  };

  useEffect(() => {
    SingleOrder();
    getMessage();
  }, [tag]);

  return OrderFound ? (
    Orderjson?.length ? (
      <div className="container-fluid">
        {/* Page Heading */}
        <div className="order-details-head d-flex justify-content-between align-items-center">
          <div>
            {/* <h2 className="h3 mb-2 text-gray-800">Orders</h2> */}
            <p className="mt-3">
              <span className="BackButton" onClick={() => navigate(-1)}>
                <i className="fas fa-arrow-left backbtn"></i>
              </span>
              <span className="h2 mb-2 text-secondary ">
                {" "}
                {Orderjson[0].name}{" "}
              </span>
              {(Orderjson[0]?.tags ?? "")
                .split(",")
                .map((t) => t.trim())
                .filter((t) => t)
                .map((t, idx) => (
                  <CBadge key={idx} color="dark text-white" className="CBadge" style={{ marginRight: 4 }}>
                    {capitalizeWords(t)}
                  </CBadge>
                ))}
              <CBadge color="info text-white" className="CBadge">
                {fulfilled}
              </CBadge>
            </p>
            <p className="mb-2 text-secondary">{OrderDate} </p>
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* <span className="testOrder" onClick={handleMarkAsSeen}>
              Mark as Seen
            </span> */}

            {/* The flag the slide asks for at the top of the order. It has to be
                readable before anyone scrolls: an order with modifications is
                made differently, and the details are further down the page. */}
            {specialModifications.length > 0 && (
              <span className="special-modifications-flag">
                {lang === "en" ? "Special Modifications" : "特殊修改"}
              </span>
            )}

            {/* Consumer orders only. A customer who writes in for "the same
                insoles again" gets a checkout built from THIS order's pairs,
                sizes included, instead of a draft typed by hand in Shopify admin
                (which has nowhere to put a size — see #4972). */}
            {orderType === "b2c" && !orderCancelled && (
              <CButton color="dark" variant="outline" size="sm" onClick={openReorder}>
                {lang === "en" ? "Reorder for this customer" : "为此客户再次下单"}
              </CButton>
            )}

            {tag === "test" ? (
              <span className="testOrder" onClick={handleRemoveAsTest}>
                {lang === "en" ? "Remove from Test Order" : "取消测试订单标记"}
              </span>
            ) : (
              <span className="testOrder" onClick={handleMarkAsTest}>
                {lang === "en" ? "Mark as Test Order" : "标记为测试订单"}
              </span>
            )}
          </div>

          {/* {Orderjson[0]?.tags
            ?.split(",")
            .map((tag) => tag.trim().toLowerCase())
            .includes("test order") && (
            <h2 className="testOrder">Test Order</h2>
          )} */}
        </div>

        <div className="order-detail-body">
          <div key={`singleproduct`}>
            <div className="row">
              <div className="col-md-12">
                <div className="card shadow mb-4"></div>
              </div>
              <div className="col-md-4">
                <div className="card shadow mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-12">
                        <h5
                          className={`order-details-title ${
                            !Orderjson[0]?.customer?.email
                              ? "missing-field-highlight"
                              : ""
                          }`}
                        >
                          {lang === "en" ? "Customer Info " : "客户信息 "}+{" "}
                          <span> ( {playerID} ) </span>
                        </h5>
                        <div>
                          <ul className="enterprisedetails">
                            <li>
                              {Orderjson[0]?.customer?.first_name +
                                " " +
                                Orderjson[0]?.customer?.last_name}
                            </li>
                            <li>{Orderjson[0]?.customer?.email}</li>
                          </ul>
                          {Orderjson[0]?.scan_fit_link ? (
                            <div className="mt-2">
                              <div className="small text-secondary mb-1">
                                {lang === "en" ? "Scan & fit link (send to the customer)" : "扫描与定制链接（发送给客户）"}
                              </div>
                              <div className="d-flex gap-2">
                                <input
                                  id="scanFitLink"
                                  className="form-control form-control-sm"
                                  readOnly
                                  value={Orderjson[0].scan_fit_link}
                                  onFocus={(e) => e.target.select()}
                                />
                                <CButton size="sm" color="secondary" onClick={() => copyLink(Orderjson[0].scan_fit_link, "Scan & fit link")}>
                                  {lang === "en" ? "Copy" : "复制"}
                                </CButton>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-12">
                        <h5 className="order-details-title">
                          {lang === "en" ? "Enterprise Info" : "企业信息"}
                        </h5>
                        <div>
                          <ul className="enterprisedetails">
                            <li>
                              <span>Enterprise Name</span>:{" "}
                              {OrderDBData?.[0]?.EnterpriseName}
                            </li>
                            <li>
                              <span>Team</span>:{" "}
                              {OrderDBData?.[0]?.TeamName}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="order-details-title">
                            {lang === "en" ? "Shipping Address" : "邮寄地址"}
                          </h5>
                          <button
                            onClick={openModal}
                            className="btn btn-link p-0"
                            title={
                              lang === "en"
                                ? "Edit Shipping Address"
                                : "编辑邮寄地址"
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="20px"
                              viewBox="0 -960 960 960"
                              width="20px"
                              fill="#000000"
                            >
                              <path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" />
                            </svg>
                          </button>
                        </div>
                        <div>
                          <p>
                            {Orderjson[0]?.shipping_address?.first_name}{" "}
                            {Orderjson[0]?.shipping_address?.last_name}
                          </p>
                          <p>{Orderjson[0]?.shipping_address?.address1} </p>
                          <p>{Orderjson[0]?.shipping_address?.address2} </p>
                          <p>
                            {Orderjson[0]?.shipping_address?.city}{" "}
                            {Orderjson[0]?.shipping_address?.province_code}{" "}
                            {Orderjson[0]?.shipping_address?.zip}{" "}
                            {Orderjson[0]?.shipping_address?.country}{" "}
                          </p>
                          <br />
                        </div>

                        {/* Modal */}
                        {isModalOpen && (
                          <div
                            className="modal d-block"
                            tabIndex="-1"
                            role="dialog"
                            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                          >
                            <div
                              className="modal-dialog modal-dialog-centered modal-xl"
                              role="document"
                            >
                              <div className="modal-content">
                                <div className="modal-header">
                                  <h5 className="modal-title">
                                    {lang === "en"
                                      ? "Edit Shipping Address"
                                      : "编辑邮寄地址"}
                                  </h5>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    height="20px"
                                    viewBox="0 -960 960 960"
                                    width="20px"
                                    fill="#000000"
                                    onClick={closeModal}
                                  >
                                    <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
                                  </svg>
                                </div>
                                <div
                                  className="modal-body"
                                  style={{
                                    maxHeight: "85vh",
                                    overflowY: "scroll",
                                    scrollbarWidth: "none", // Firefox
                                    msOverflowStyle: "none", // IE 10+
                                  }}
                                >
                                  {message.text && (
                                    <div
                                      className={`alert ${
                                        message.type === "success"
                                          ? "alert-success"
                                          : "alert-danger"
                                      }`}
                                    >
                                      {message.text}
                                    </div>
                                  )}
                                  <form onSubmit={handleSubmit}>
                                    <div className="row">
                                      <div className="col-12 col-md-6 mb-3">
                                        <label
                                          htmlFor="first_name"
                                          className="form-label"
                                        >
                                          {lang === "en"
                                            ? "First Name"
                                            : "名字"}
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          id="first_name"
                                          name="first_name"
                                          value={formData.first_name}
                                          onChange={handleChange}
                                          required
                                        />
                                      </div>
                                      <div className="col-12 col-md-6 mb-3">
                                        <label
                                          htmlFor="last_name"
                                          className="form-label"
                                        >
                                          {lang === "en" ? "Last Name" : "姓氏"}
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          id="last_name"
                                          name="last_name"
                                          value={formData.last_name}
                                          onChange={handleChange}
                                          required
                                        />
                                      </div>
                                    </div>

                                    <div className="mb-3">
                                      <label
                                        htmlFor="company"
                                        className="form-label"
                                      >
                                        {lang === "en"
                                          ? "Company (Optional)"
                                          : "公司 (可选)"}
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        id="company"
                                        name="company"
                                        value={formData.company || ""}
                                        onChange={handleChange}
                                      />
                                    </div>

                                    <div className="mb-3">
                                      <label
                                        htmlFor="address1"
                                        className="form-label"
                                      >
                                        {lang === "en"
                                          ? "Address Line 1"
                                          : "地址行 1"}
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        id="address1"
                                        name="address1"
                                        value={formData.address1}
                                        onChange={handleChange}
                                        required
                                      />
                                    </div>

                                    <div className="mb-3">
                                      <label
                                        htmlFor="address2"
                                        className="form-label"
                                      >
                                        {lang === "en"
                                          ? "Address Line 2 (Optional)"
                                          : "地址行 2 (可选)"}
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        id="address2"
                                        name="address2"
                                        value={formData.address2 || ""}
                                        onChange={handleChange}
                                      />
                                    </div>

                                    <div className="row">
                                      <div className="col-12 col-md-6 mb-3">
                                        <label
                                          htmlFor="country"
                                          className="form-label"
                                        >
                                          {lang === "en" ? "Country" : "国家"}
                                        </label>
                                        <select
                                          className="form-select"
                                          id="country"
                                          name="country_code"
                                          value={formData.country_code || ""}
                                          onChange={handleCountryChange}
                                          required
                                        >
                                          <option value="" disabled>
                                            {lang === "en" ? "Select a country" : "选择国家"}
                                          </option>
                                          {/* An order whose country is not offered (not in our
                                              list, or not shipped to) keeps it selected rather
                                              than being silently moved. */}
                                          {formData.country_code &&
                                            !countryOptions.some(
                                              (c) => c.code === formData.country_code
                                            ) && (
                                              <option value={formData.country_code}>
                                                {formData.country || formData.country_code}
                                              </option>
                                            )}
                                          {countryOptions.map((c) => (
                                            <option key={c.code} value={c.code}>
                                              {c.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="col-12 col-md-6 mb-3">
                                        <label
                                          htmlFor="province"
                                          className="form-label"
                                        >
                                          {lang === "en"
                                            ? "State/Province"
                                            : "州/省"}
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          id="province"
                                          name="province"
                                          value={formData.province}
                                          onChange={handleChange}
                                          required
                                        />
                                      </div>
                                    </div>

                                    <div className="row">
                                      <div className="col-12 col-md-6 mb-3">
                                        <label
                                          htmlFor="city"
                                          className="form-label"
                                        >
                                          {lang === "en" ? "City" : "城市"}
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          id="city"
                                          name="city"
                                          value={formData.city}
                                          onChange={handleChange}
                                          required
                                        />
                                      </div>

                                      <div className="col-12 col-md-6 mb-3">
                                        <label
                                          htmlFor="zip"
                                          className="form-label"
                                        >
                                          {lang === "en"
                                            ? "ZIP/Postal Code"
                                            : "邮政编码"}
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          id="zip"
                                          name="zip"
                                          value={formData.zip}
                                          onChange={handleChange}
                                          required
                                        />
                                      </div>
                                    </div>

                                    <div className="mb-3">
                                      <label
                                        htmlFor="phone"
                                        className="form-label"
                                      >
                                        {lang === "en"
                                          ? "Phone (Optional)"
                                          : "电话 (可选)"}
                                      </label>
                                      <input
                                        type="tel"
                                        className="form-control"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone || ""}
                                        onChange={handleChange}
                                      />
                                    </div>

                                    {/* Hidden fields for latitude and longitude */}
                                    <input
                                      type="hidden"
                                      name="latitude"
                                      value={formData.latitude}
                                    />
                                    <input
                                      type="hidden"
                                      name="longitude"
                                      value={formData.longitude}
                                    />

                                    <div className="modal-footer">
                                      <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                        disabled={isLoading}
                                      >
                                        {lang === "en" ? "Cancel" : "取消"}
                                      </button>
                                      <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                      >
                                        {isLoading ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                              aria-hidden="true"
                                            ></span>
                                            {lang === "en"
                                              ? "Saving..."
                                              : "保存中..."}
                                          </>
                                        ) : lang === "en" ? (
                                          "Save Changes"
                                        ) : (
                                          "保存更改"
                                        )}
                                      </button>
                                    </div>
                                  </form>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order-level production status + tracking (replaces the
                deprecated per-line-item status/tracking editing) */}
            <div className="row">
              <div className="col-md-12">
                <div className="card shadow mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="order-details-title mb-0">
                        {lang === "en" ? "Production Status" : "生產狀態"}
                      </h5>
                      {orderCancelled ? (
                        <span className="badge bg-danger text-white">
                          {lang === "en" ? "Cancelled" : "取消"}
                        </span>
                      ) : (
                        <select
                          className="custom-select"
                          style={{ maxWidth: 220 }}
                          value={productionStatus}
                          onChange={changeProductionStatus}
                        >
                          <option value="awaiting_review">
                            {lang === "en" ? "Awaiting Review" : "等待审核"}
                          </option>
                          <option value="reviewed">
                            {lang === "en" ? "Reviewed" : "已审核"}
                          </option>
                          <option value="manufactured">
                            {lang === "en" ? "Manufactured" : "已生产"}
                          </option>
                          <option value="shipped">
                            {lang === "en" ? "Shipped" : "已发货"}
                          </option>
                        </select>
                      )}
                    </div>

                    {trackingInfo.number ? (
                      <div className="mt-3">
                        <h6 className="order-details-title">
                          {lang === "en" ? "Tracking Info" : "跟踪信息"}
                          <i
                            className="m-2"
                            style={{ cursor: "pointer" }}
                            onClick={openEditTracking}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="20px"
                              viewBox="0 -960 960 960"
                              width="20px"
                              fill="#000000"
                            >
                              <path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" />
                            </svg>
                          </i>
                        </h6>
                        <h6>
                          {lang === "en" ? "Tracking Number" : "快递单号"}:{" "}
                          {trackingInfo.number}
                        </h6>
                        <h6>
                          {lang === "en" ? "Tracking Company:" : "快递公司 :"}{" "}
                          {trackingInfo.company}
                        </h6>
                        <h6>
                          {lang === "en" ? "Tracking URL:" : "跟踪网址 :"}{" "}
                          {/* Only a real web address becomes a link. A value
                              stored before the URL rule existed is shown as
                              text, never handed to href. */}
                          {isValidTrackingUrl(trackingInfo.url) ? (
                            <a
                              href={trackingInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {trackingInfo.url}
                            </a>
                          ) : (
                            trackingInfo.url
                          )}
                        </h6>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {(Orderjson[0]?.tags?.includes("test") ||
              (Orderjson[0]?.discount_codes || [])
                .map((item) => item.code)
                .includes("XTEST100")) &&
            localStorage.getItem("tag") === "Dake International Co. Ltd." ? (
              <>
                <div className="d-flex justify-content-center align-items-center vh-100">
                  <h1>Test Order</h1>
                </div>
              </>
            ) : (
              <>
                {Orderjson[0]?.line_items
                  ?.filter((item) => !canceledFiltered?.includes(item?.id))
                  .map((itemVal, i) => {
                    return (
                      <OrderSingle
                        key={i}
                        itemVal={itemVal}
                        i={i}
                        lang={lang}
                        OrderDBData={OrderDBData}
                        orderSlug={params?.id}
                        editable={!Orderjson[0]?.cancelled && Orderjson[0]?.production_status === "awaiting_review"}
                        onSaved={SingleOrder}
                      />
                    );
                  })}
              </>
            )}

            <div className="row">
              <div className="col-md-12">
                <div className="card shadow mb-4">
                  <div className="card-body">
                    <ul className="list-groupuu">
                      {OrderMessage?.message?.map((msgVal, i) => {
                        return (
                          // <li className="list-group-item" key={i}>
                          //   <input
                          //     className="form-check-input"
                          //     type="checkbox"
                          //     checked={msgVal.MarkAsRead}
                          //     id="flexCheckDefaultflag"
                          //     onChange={(e) => MessageCheck(e, i, msgVal.ID)}
                          //   // onChange={(e) =>
                          //   //   setOrderMessage((presVal) => ({ ...presVal }))
                          //   // }
                          //   />
                          //   {msgVal.Message}
                          // </li>

                          <li className="list-group-item" key={i}>
                            <div className="grv-comment-ref">
                              <span className="grv-comment-by">
                                {msgVal.UserName}
                              </span>
                              <span className="grv-comment-time">
                                {formatTimestamp(
                                  changeGMTFormate(msgVal?.MessageTime)
                                )}
                              </span>
                            </div>
                            <div className="grv-comment-data">
                              <span className="grv-comment-circle text-light">
                                {AllWordFirstChar(msgVal?.UserName) ?? "GA"}
                              </span>
                              <p className="grv-comment-comment">
                                {msgVal.Message}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <CForm>
                      <CFormTextarea
                        id="exampleFormControlTextarea1"
                        label={lang === "en" ? "Message" : "信息"}
                        rows={3}
                        value={OrderMessage.msgValue}
                        onChange={(e) =>
                          setOrderMessage((presVal) => ({
                            ...presVal,
                            msgValue: e.target.value,
                          }))
                        }
                      ></CFormTextarea>
                      <br />
                      {OrderMessage.msgLoading ? (
                        <CLoadingButton color="dark" type="button">
                          {lang === "en" ? "Add Message" : "添加留言"}
                        </CLoadingButton>
                      ) : (
                        <CButton color="dark" onClick={HandleMessage}>
                          {lang === "en" ? "Add Message" : "添加留言"}
                        </CButton>
                      )}
                    </CForm>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card shadow mb-4">
                  <div className="card-body">
                    {/* Directly above the Note box, as designed — but OUTSIDE
                        the textarea. Inside it, the first factory comment saved
                        over the top would erase the trainer's request with no
                        record of what it said. Here the Note stays 100% the
                        factory's, and this cannot be edited or lost. */}
                    {specialModifications.length > 0 && (
                      <div className="special-modifications-summary">
                        <div className="title">
                          {lang === "en"
                            ? "Special modifications"
                            : "特殊修改"}
                        </div>
                        {specialModifications.map((line, idx) => (
                          <div className="item" key={idx}>
                            {line}
                          </div>
                        ))}
                      </div>
                    )}

                    {shopifyNote && (
                      <div className="shopify-note-summary">
                        <div className="title">
                          {lang === "en"
                            ? "Note from Shopify (customer or app)"
                            : "来自 Shopify 的备注（客户或应用）"}
                        </div>
                        <div className="item">{shopifyNote}</div>
                      </div>
                    )}

                    <CForm>
                      <CFormTextarea
                        id="orderNoteTextarea"
                        label={lang === "en" ? "Note" : "笔记"}
                        rows={3}
                        value={note}
                        onChange={(e) => {
                          setNote(e.target.value);
                        }}
                      ></CFormTextarea>
                      <br />
                      {tagLoading ? (
                        <CLoadingButton color="dark">
                          {lang === "en" ? "Add Note" : "添加註釋"}
                        </CLoadingButton>
                      ) : (
                        <CButton color="dark" onClick={HandleUpdate}>
                          {lang === "en" ? "Add Note" : "添加註釋"}
                        </CButton>
                      )}
                    </CForm>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Order-level tracking modal — used both to ship (set status + create
            the fulfillment) and to edit tracking on an already-shipped order. */}
        <CModal
          alignment="center"
          visible={trackingModal.visible}
          onClose={() => setTrackingModal((m) => ({ ...m, visible: false }))}
        >
          <CModalHeader>
            <CModalTitle>
              {lang === "en" ? "Tracking Info" : "跟踪信息"}
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            <div className="mb-3">
              <label className="form-label">
                {lang === "en" ? "Tracking Number" : "快递单号"}
              </label>
              <input
                className="form-control"
                value={trackingModal.number}
                onChange={(e) =>
                  setTrackingModal((m) => ({ ...m, number: e.target.value }))
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label">
                {lang === "en" ? "Tracking URL" : "跟踪网址"}
              </label>
              <input
                className="form-control"
                type="url"
                placeholder="https://"
                value={trackingModal.url}
                onChange={(e) =>
                  setTrackingModal((m) => ({ ...m, url: e.target.value }))
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Shipping Company Name</label>
              <input
                className="form-control"
                value={trackingModal.company}
                onChange={(e) =>
                  setTrackingModal((m) => ({ ...m, company: e.target.value }))
                }
              />
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton
              color="secondary"
              onClick={() => setTrackingModal((m) => ({ ...m, visible: false }))}
            >
              {lang === "en" ? "Close" : "关闭"}
            </CButton>
            {trackingModal.loading ? (
              <CLoadingButton color="info">Submit</CLoadingButton>
            ) : (
              <CButton color="primary" onClick={submitTracking}>
                Submit
              </CButton>
            )}
          </CModalFooter>
        </CModal>

        {/* Reorder for this customer: the pairs are shown read-only (they are
            copied as they are); the only input is an optional discount code.
            The result is two links: the one to SEND, which builds a fresh
            checkout every time it is opened and is good for a year, and the
            checkout itself for a customer who is paying right now (Shopify
            drops an untouched cart after about ten days). */}
        <CModal
          alignment="center"
          visible={reorderModal.visible}
          onClose={() => setReorderModal((m) => ({ ...m, visible: false }))}
        >
          <CModalHeader>
            <CModalTitle>
              {lang === "en" ? "Reorder for this customer" : "为此客户再次下单"}
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            {reorderModal.result ? (
              <div>
                <p className={reorderModal.result.discount_applied === false ? "text-danger" : ""}>
                  {reorderModal.result.message}
                </p>
                <div className="mb-3">
                  <label className="form-label">
                    <strong>{lang === "en" ? "Link to send the customer" : "发送给客户的链接"}</strong>{" "}
                    <span className="text-secondary">
                      {lang === "en" ? "(good for a year, opens a fresh checkout every time)" : "（一年内有效，每次打开都会生成新的结账页）"}
                    </span>
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      className="form-control"
                      readOnly
                      value={reorderModal.result.reorder_link}
                      onFocus={(e) => e.target.select()}
                    />
                    <CButton
                      color="primary"
                      onClick={() => copyLink(reorderModal.result.reorder_link, "Link")}
                    >
                      {lang === "en" ? "Copy" : "复制"}
                    </CButton>
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label">
                    {lang === "en" ? "Direct checkout" : "直接结账"}{" "}
                    <span className="text-secondary">
                      {lang === "en" ? "(for a customer paying right now; Shopify drops it after ~10 days)" : "（客户立即付款用；约 10 天后失效）"}
                    </span>
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      className="form-control"
                      readOnly
                      value={reorderModal.result.checkout_url}
                      onFocus={(e) => e.target.select()}
                    />
                    <CButton
                      color="secondary"
                      onClick={() => copyLink(reorderModal.result.checkout_url, "Checkout link")}
                    >
                      {lang === "en" ? "Copy" : "复制"}
                    </CButton>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-secondary">
                  {lang === "en"
                    ? `Size, gender, nickname and shoe are copied exactly as they were made; you can change how many of each pair and which model. The customer (${Orderjson[0]?.customer?.email || "no email"}) pays at Shopify's checkout, where they can still enter a code; nothing is created here until they do.`
                    : `尺码、性别、昵称和鞋子按原样复制；可以修改每双的数量和型号。客户（${Orderjson[0]?.customer?.email || "无邮箱"}）在 Shopify 结账页付款，仍可输入折扣码；付款前不会创建任何订单。`}
                </p>
                <div className="mb-3">
                  {(Orderjson[0]?.line_items || []).map((li, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
                      <CFormSelect
                        id={`reorderPairs${idx}`}
                        size="sm"
                        style={{ width: "5.5em" }}
                        aria-label={lang === "en" ? "Number of pairs" : "数量"}
                        value={reorderModal.changes[idx]?.pairs ?? li.quantity ?? 1}
                        onChange={(e) => setReorderChange(idx, { pairs: Number(e.target.value) })}
                      >
                        {[1, 2, 3, 4].map((n) => (
                          <option key={n} value={n}>{n} ×</option>
                        ))}
                      </CFormSelect>
                      <CFormSelect
                        id={`reorderModel${idx}`}
                        size="sm"
                        style={{ width: "11em" }}
                        aria-label={lang === "en" ? "Model" : "型号"}
                        value={reorderModal.changes[idx]?.finish ?? li.finish ?? ""}
                        onChange={(e) => setReorderChange(idx, { finish: e.target.value })}
                      >
                        {!REORDER_MODELS.some((m) => m.sku === (reorderModal.changes[idx]?.finish ?? li.finish)) ? (
                          <option value={li.finish || ""}>{li.finish || "?"}</option>
                        ) : null}
                        {REORDER_MODELS.map((m) => (
                          <option key={m.sku} value={m.sku}>{m.label}</option>
                        ))}
                      </CFormSelect>
                      <span className="text-secondary">
                        {li.shoe_size ? `${(li.shoe_size_gender || "").charAt(0).toUpperCase()} ${li.shoe_size}` : ""}
                        {li.nickname ? ` · "${li.nickname}"` : ""}
                        {li.shoe_specific && li.shoe_name ? ` · ${li.shoe_name}` : ""}
                        {!li.shoe_size || !li.nickname ? (
                          <span className="text-danger">
                            {" "}
                            ({lang === "en" ? "missing size or nickname" : "缺少尺码或昵称"})
                          </span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="form-label" htmlFor="reorderDiscountCode">
                    {lang === "en" ? "Discount code (optional, applied for them)" : "折扣码（可选，自动应用）"}
                  </label>
                  <input
                    id="reorderDiscountCode"
                    className="form-control"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. QUIZ15"
                    value={reorderModal.discountCode}
                    onChange={(e) => setReorderModal((m) => ({ ...m, discountCode: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </CModalBody>
          <CModalFooter>
            <CButton
              color="secondary"
              onClick={() => setReorderModal((m) => ({ ...m, visible: false }))}
            >
              {lang === "en" ? "Close" : "关闭"}
            </CButton>
            {!reorderModal.result &&
              (reorderModal.loading ? (
                <CLoadingButton color="info">
                  {lang === "en" ? "Preparing checkout…" : "正在准备…"}
                </CLoadingButton>
              ) : (
                <CButton color="primary" onClick={submitReorder}>
                  {lang === "en" ? "Create checkout link" : "生成结账链接"}
                </CButton>
              ))}
          </CModalFooter>
        </CModal>

        <Toaster />
      </div>
    ) : (
      <>
        <div style={{ height: "100%" }} role="status">
          <CircleLoader className="LoaderSingleOrder" color="#1ca1ee" />
        </div>
      </>
    )
  ) : (
    <div>
      <h1 className="text-dark mx-auto">
        {lang === "en" ? "Order not found" : "未找到订单"}
      </h1>
    </div>
  );
};

export default OrderDetailsPage;
